import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Plus, X, Trash2 } from 'lucide-react';
import { useRegistration } from '../../../../contexts/registration/hooks';
import toast from 'react-hot-toast';

interface BusinessStepThreeProps {
  loading?: boolean;
}

interface Break {
  id: string;
  startTime: string;
  endTime: string;
}

interface DayHours {
  isActive: boolean;
  startTime: string;
  endTime: string;
  breaks: Break[];
}

const DAYS = [
  { id: 'sunday', name: 'ראשון', short: 'א' },
  { id: 'monday', name: 'שני', short: 'ב' },
  { id: 'tuesday', name: 'שלישי', short: 'ג' },
  { id: 'wednesday', name: 'רביעי', short: 'ד' },
  { id: 'thursday', name: 'חמישי', short: 'ה' },
  { id: 'friday', name: 'שישי', short: 'ו' },
  { id: 'saturday', name: 'שבת', short: 'ש' },
];

export function BusinessStepThree({ loading }: BusinessStepThreeProps) {
  const { updateStep, getStepData } = useRegistration();

  const [showBreakForm, setShowBreakForm] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [breakFormData, setBreakFormData] = useState({ startTime: '12:00', endTime: '13:00' });

  const [formData, setFormData] = useState<Record<string, DayHours>>(() => {
    const savedData = getStepData(3)?.hours || {};
    const defaultHours = { startTime: '09:00', endTime: '17:00', breaks: [] as Break[] };

    return DAYS.reduce((acc, day) => {
      const existing = savedData[day.id];
      acc[day.id] = existing ?? { ...defaultHours, isActive: day.id !== 'saturday' };
      return acc;
    }, {} as Record<string, DayHours>);
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const validateBreaks = (dayId: string, breaks: Break[]): string | null => {
    const dayHours = formData[dayId];
    const dayStart = timeToMinutes(dayHours.startTime);
    const dayEnd = timeToMinutes(dayHours.endTime);

    const sorted = [...breaks].sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
    let lastEnd = dayStart;

    for (const b of sorted) {
      const s = timeToMinutes(b.startTime);
      const e = timeToMinutes(b.endTime);

      if (s < dayStart || e > dayEnd) return 'הפסקה חייבת להיות בתוך שעות העבודה';
      if (s >= e) return 'שעת התחלה של הפסקה חייבת להיות לפני שעת הסיום';
      if (s < lastEnd) return 'הפסקות לא יכולות לחפוף';
      lastEnd = e;
    }
    return null;
  };

  const validateForm = (): boolean => {
    const nextErrors: Record<string, string> = {};
    for (const day of DAYS) {
      const d = formData[day.id];
      if (!d.isActive) continue;

      const start = timeToMinutes(d.startTime);
      const end = timeToMinutes(d.endTime);
      if (start >= end) {
        nextErrors[day.id] = 'שעת התחלה חייבת להיות לפני שעת הסיום';
        continue;
      }

      const breakErr = validateBreaks(day.id, d.breaks);
      if (breakErr) nextErrors[day.id] = breakErr;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleAddBreak = () => {
    if (!selectedDay) return;

    const candidate = { id: crypto.randomUUID(), ...breakFormData };
    const err = validateBreaks(selectedDay, [...formData[selectedDay].breaks, candidate]);
    if (err) {
      setErrors((prev) => ({ ...prev, [selectedDay]: err }));
      setShowBreakForm(false);
      setSelectedDay(null);
      setBreakFormData({ startTime: '12:00', endTime: '13:00' });
      toast.error(err, {
        duration: 4000,
        position: 'top-center',
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [selectedDay]: {
        ...prev[selectedDay],
        breaks: [...prev[selectedDay].breaks, candidate],
      },
    }));

    setShowBreakForm(false);
    setSelectedDay(null);
    setBreakFormData({ startTime: '12:00', endTime: '13:00' });
    setErrors((prev) => ({ ...prev, [selectedDay!]: '' }));
    toast.success('ההפסקה נוספה בהצלחה', {
      duration: 2000,
      position: 'top-center',
    });
  };

  const handleDeleteBreak = (dayId: string, breakId: string) => {
    setFormData((prev) => ({
      ...prev,
      [dayId]: {
        ...prev[dayId],
        breaks: prev[dayId].breaks.filter((b) => b.id !== breakId),
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (!validateForm()) return;

    try {
      await updateStep(3, { hours: formData });

      localStorage.setItem(
        'business_hours',
        JSON.stringify({ regular_hours: formData, special_dates: [] })
      );

      toast.success('שעות הפעילות נשמרו בהצלחה! ממשיך לשלב הבא...');
    } catch (err: any) {

      toast.error(err?.message || 'שגיאה בשמירת שעות הפעילות');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-3">
        {DAYS.map((day, index) => {
          const dayHours = formData[day.id];

          return (
            <motion.div
              key={day.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="relative"
            >
              <div
                className={`relative bg-white rounded-lg overflow-hidden transition-colors ${
                  dayHours.isActive
                    ? 'border border-gray-300'
                    : 'border border-gray-200'
                }`}
              >
                <div className="p-3">
                  {/* שורה ראשית - Toggle + שם יום + שעות */}
                  <div className="flex items-center gap-3">
                    {/* Toggle */}
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={dayHours.isActive}
                        onChange={(e) => {
                          setFormData((prev) => ({
                            ...prev,
                            [day.id]: {
                              ...prev[day.id],
                              isActive: e.target.checked,
                            },
                          }));
                          if (errors[day.id]) {
                            setErrors((prev) => ({ ...prev, [day.id]: '' }));
                          }
                        }}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>

                    {/* שם היום */}
                    <h3 className={`font-medium text-sm w-16 transition-colors ${
                      dayHours.isActive ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {day.name}
                    </h3>

                    {/* שדות זמן - רק אם פעיל */}
                    {dayHours.isActive ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input
                          type="time"
                          className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all text-sm"
                          value={dayHours.startTime}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              [day.id]: { ...prev[day.id], startTime: e.target.value },
                            }));
                            if (errors[day.id]) setErrors((p) => ({ ...p, [day.id]: '' }));
                          }}
                        />
                        <span className="text-gray-400 text-sm">-</span>
                        <input
                          type="time"
                          className="px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all text-sm"
                          value={dayHours.endTime}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              [day.id]: { ...prev[day.id], endTime: e.target.value },
                            }));
                            if (errors[day.id]) setErrors((p) => ({ ...p, [day.id]: '' }));
                          }}
                        />
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 flex-1">סגור</span>
                    )}
                  </div>

                  {/* שורה שנייה - הפסקות (רק אם יש או אפשר להוסיף) */}
                  {dayHours.isActive && (
                    <>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-2 flex-wrap flex-1">
                          <span className="text-xs font-medium text-gray-600">הפסקות:</span>
                          {dayHours.breaks.length > 0 ? (
                            dayHours.breaks.map((b) => (
                              <div
                                key={b.id}
                                className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 border border-gray-200 rounded text-xs text-gray-700"
                              >
                                <span>{b.startTime}-{b.endTime}</span>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteBreak(day.id, b.id)}
                                  className="text-gray-400 hover:text-red-600 active:scale-95 transition-all"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">אין הפסקות</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedDay(day.id);
                            setShowBreakForm(true);
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded active:scale-95 transition-all flex-shrink-0"
                        >
                          <Plus className="h-3 w-3" />
                          הוסף
                        </button>
                      </div>
                      {errors[day.id] && (
                        <p className="text-xs text-red-600 mt-1">
                          {errors[day.id]}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Break Form Modal */}
      <AnimatePresence>
        {showBreakForm && (
          <div className="fixed inset-0 z-50 overflow-hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => {
                setShowBreakForm(false);
                setSelectedDay(null);
              }}
            />
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="bg-white rounded-2xl p-6 w-full max-w-md relative shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[17px] font-semibold text-gray-900">הוספת הפסקה</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowBreakForm(false);
                      setSelectedDay(null);
                    }}
                    className="p-1.5 text-gray-600 rounded-lg active:scale-95 transition-transform"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">שעת התחלה</label>
                    <input
                      type="time"
                      value={breakFormData.startTime}
                      onChange={(e) => setBreakFormData((p) => ({ ...p, startTime: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">שעת סיום</label>
                    <input
                      type="time"
                      value={breakFormData.endTime}
                      onChange={(e) => setBreakFormData((p) => ({ ...p, endTime: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none transition-all text-sm"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBreakForm(false);
                      setSelectedDay(null);
                    }}
                    className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 active:scale-95 transition-all"
                  >
                    ביטול
                  </button>
                  <button
                    type="button"
                    onClick={handleAddBreak}
                    className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 active:scale-95 transition-all"
                  >
                    הוסף
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Submit Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="pt-4"
      >
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 rounded-xl text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>טוען...</span>
            </>
          ) : (
            <>
              <span>המשך לשלב הבא</span>
              <span>←</span>
            </>
          )}
        </button>
      </motion.div>
    </form>
  );
}
